import React, {useCallback, useContext, useEffect, useState} from "react";
import {isBoolean} from "lodash";
import moment from "moment-timezone";
import {TableDateSearchContext} from "../../admin/Context/TableDateSearchContext.jsx";
import {NOOP} from "../../utils/NOOP.jsx";

export const useItemListViewActions = (shortcutSearchOptions = [], log = NOOP, defaultShortcutSearchOptions) => {
    const {
        onClearDebouncedSearchText,
        setSearchDate,
        setSearchByCreated,
        searchByCreated,
        setSearchStatuses,
        formValues,
        setFormValues
    } = useContext(TableDateSearchContext);

    const [selectedOption, setSelectedOption] = useState(defaultShortcutSearchOptions ?? "");

    useEffect(() => {
        const selectedShortcutOptionSettings = shortcutSearchOptions?.find(option => option.key === selectedOption);
        setFormValues(prev => ({
            ...prev,
            selectedOption,
            ...(selectedShortcutOptionSettings && {selectedOptionShortcut: selectedShortcutOptionSettings})
        }));
    }, [selectedOption]);

    useEffect(() => {
        if (defaultShortcutSearchOptions) {
            const selectedShortcutOptionSettings = shortcutSearchOptions?.find(
                option => option.key === defaultShortcutSearchOptions
            );
            if (selectedShortcutOptionSettings) {
                onShortcutClick(selectedShortcutOptionSettings);
            }
        }
    }, [defaultShortcutSearchOptions]);

    const isDateSearchMatchShortcutOption = useCallback(
        values => {
            const selectedShortcutOptionSettings = shortcutSearchOptions?.find(option => option.key === selectedOption);
            return (
                selectedShortcutOptionSettings &&
                selectedShortcutOptionSettings?.isChecked &&
                selectedShortcutOptionSettings?.isChecked(values)
            );
        },
        [selectedOption, shortcutSearchOptions]
    );

    const onShortcutClick = useCallback(
        option => {
            const transformOption = {
                value: option ? option?.value : null,
                statuses: option?.statuses?.length ? option?.statuses : null,
                searchByCreated: isBoolean(option?.searchByCreated) ? option?.searchByCreated : true,
                datePicked: option?.datePicked
                    ? option?.datePicked()
                    : [moment().startOf("month"), moment().endOf("month")]
            };

            setSelectedOption(transformOption.value);
            setSearchStatuses(transformOption.statuses);
            setSearchByCreated(transformOption.searchByCreated);
            setSearchDate(transformOption.datePicked);
            onClearDebouncedSearchText();
            log("user click on order shortcut button", {option: transformOption});
        },
        [log, onClearDebouncedSearchText, setSearchByCreated, setSearchDate, setSearchStatuses]
    );

    const onSearchByCreatedAtChange = useCallback(() => {
        if (selectedOption && !isDateSearchMatchShortcutOption({...formValues, searchByCreated: !searchByCreated})) {
            setSelectedOption(null);
            setSearchStatuses(null);
        }
        setSearchByCreated(prevState => !prevState);
    }, [
        selectedOption,
        isDateSearchMatchShortcutOption,
        formValues,
        searchByCreated,
        setSearchByCreated,
        setSearchStatuses
    ]);

    const onDatePicker = useCallback(
        selectedDate => {
            if (selectedOption && !isDateSearchMatchShortcutOption({...formValues, searchDate: selectedDate})) {
                setSelectedOption(null);
                setSearchStatuses(null);
            }
            setSearchDate(selectedDate);
        },
        [formValues, isDateSearchMatchShortcutOption, selectedOption, setSearchDate, setSearchStatuses]
    );

    return {
        onShortcutClick,
        onDatePicker,
        onSearchByCreatedAtChange,
        selectedOption
    };
};
